import {
  DashboardSearchParams,
  IntegrationRequest,
  IntegrationRequestDto,
  IntegrationsDashboardType,
  SendingRequest,
  SendingRequestDto,
  SendingsDashboardType,
} from '@domain/types';

import { XhrStatus } from '@store/types';

import { ArchivedDocumentsActions } from './incoming/archived.slice';
import { ArchivedDocumentsState } from './incoming/archived.state';
import { InboxDocumentsActions } from './incoming/inbox.slice';
import { InboxDocumentsState } from './incoming/inbox.state';
import { WorklistIntegrationsState } from './integrations/worklist.state';
import { DraftDocumentsActions } from './outgoing/draft.slice';
import { DraftDocumentsState } from './outgoing/draft.state';
import { ErroredDocumentsActions } from './outgoing/errored.slice';
import { ErroredDocumentDashboardType, ErroredDocumentsState } from './outgoing/errored.state';
import { SentDocumentsActions } from './outgoing/sent.slice';
import { SentDocumentsState } from './outgoing/sent.state';

/*
 * XHR
 */

export const XHR_STATUS_IDLE: XhrStatus = { isLoading: false, isInError: false };
export const XHR_STATUS_LOADING: XhrStatus = { isLoading: true, isInError: false };
export const XHR_STATUS_ERROR: XhrStatus = { isLoading: false, isInError: true };

/*
 * ENTITIES
 */

export type BaseEntitiesState<T> = Record<string, T>;

/*
 * DASHBOARD
 */

export interface BaseDashboardViewState {
  ids: string[];
  searchParams: DashboardSearchParams;
  total: number;
  documentsRequest: XhrStatus;
}

export const INITIAL_SEARCH_PARAMS: DashboardSearchParams = {
  page: 1,
  perPage: 20,
};

export const INITIAL_DASHBOARD_VIEW_STATE: BaseDashboardViewState = {
  ids: [],
  searchParams: INITIAL_SEARCH_PARAMS,
  total: 0,
  documentsRequest: XHR_STATUS_IDLE,
};

/*
 * DETAIL
 */

/*
 * PROPS
 */

export interface DashboardSettingsOverride {
  myRequestOnly?: boolean;
}
export interface SettingsOverridesState {
  settingsOverrides: DashboardSettingsOverride;
}

export const INITIAL_SETTINGS_OVERRIDES_VIEW_STATE: DashboardSettingsOverride = {
  myRequestOnly: false,
};

/*
 * TYPES
 */

export type DocumentsState =
  | InboxDocumentsState
  | ArchivedDocumentsState
  | DraftDocumentsState
  | SentDocumentsState
  | ErroredDocumentsState;

export type DocumentsStateFromType<T> = T extends SendingsDashboardType.Inbox
  ? InboxDocumentsState
  : T extends SendingsDashboardType.Archive
    ? ArchivedDocumentsState
    : T extends SendingsDashboardType.Draft
      ? DraftDocumentsState
      : T extends SendingsDashboardType.Sent
        ? SentDocumentsState
        : T extends IntegrationsDashboardType.Worklist
          ? WorklistIntegrationsState
          : ErroredDocumentsState;

export type SendingRequestID = SendingRequest['id'];
export type IntegrationRequestID = IntegrationRequest['id'];

export type DocumentDashboardType = SendingsDashboardType | ErroredDocumentDashboardType;

export type SendingRequestsActions =
  | typeof InboxDocumentsActions
  | typeof ArchivedDocumentsActions
  | typeof DraftDocumentsActions
  | typeof SentDocumentsActions;

export type DocumentsWithErroredActions = SendingRequestsActions | typeof ErroredDocumentsActions;

export type SendingRequestFromType<T> = T extends
  | IntegrationsDashboardType.Worklist
  | IntegrationsDashboardType.Integrated
  ? IntegrationRequest
  : SendingRequest;

export type SendingRequestDtoFromType<T> = T extends
  | IntegrationsDashboardType.Worklist
  | IntegrationsDashboardType.Integrated
  ? IntegrationRequestDto
  : SendingRequestDto;
